/**
 * How a context condition is checked, shared by the do-chat URL rewrite rules and the crai-office
 * response preview (DAS-2810). The chatbot applies the same rule to block display conditions
 * (science-hub `context_settings.py`, DAS-2806), so the three must change together.
 */

/** The comparison of a condition, whatever its operand (a context key or a variable). */
export type ContextConditionPredicate =
  | { type: "exists" }
  | { type: "equals"; value: string }
  | { type: "in"; values: string[] }
  | { type: "numberCompare"; operator: "<" | "<=" | ">" | ">="; value: number };

/**
 * Resolves a context key, supporting optional dot-paths (e.g. `data.foo`).
 * Returns `undefined` when any segment is missing.
 */
export function resolveContextValue(
  conversationContext: Record<string, unknown> | null | undefined,
  key: string
): unknown {
  if (!conversationContext || !key) return undefined;
  const parts = key.split(".");
  let current: unknown = conversationContext;
  for (const part of parts) {
    if (current == null || typeof current !== "object") return undefined;
    current = (current as Record<string, unknown>)[part];
  }
  return current;
}

/**
 * Whether an already resolved value meets a condition. `null` and `undefined` are a missing value:
 * only `exists` tells them apart from the others, by failing. The other operators read the value as
 * JavaScript `String()` and `Number()` write it.
 */
export function contextConditionHolds(
  condition: ContextConditionPredicate,
  resolved: unknown
): boolean {
  if (condition.type === "exists") {
    return resolved != null;
  }
  if (resolved == null) return false;

  switch (condition.type) {
    case "equals":
      return String(resolved) === condition.value;
    case "in":
      return condition.values.includes(String(resolved));
    case "numberCompare": {
      const n = typeof resolved === "number" ? resolved : Number(resolved);
      if (Number.isNaN(n)) return false;
      switch (condition.operator) {
        case "<":
          return n < condition.value;
        case "<=":
          return n <= condition.value;
        case ">":
          return n > condition.value;
        case ">=":
          return n >= condition.value;
      }
      return false;
    }
  }
}
